import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Check, Copy, Lock, Unlock } from 'lucide-react'
import { useAuditShareActions } from '@/hooks/useAuditShare'

interface SharePopoverProps {
  shareUrl: string
  expiresAt: string
  hasPassword: boolean
  includeRemake: boolean
  hasRemake: boolean
  jobId: string
}

export function SharePopover({ shareUrl, expiresAt, hasPassword, includeRemake, hasRemake, jobId }: SharePopoverProps) {
  const { t, i18n } = useTranslation()
  const [copied, setCopied] = useState(false)
  const [password, setPassword] = useState('')
  const { create } = useAuditShareActions(jobId)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(shareUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleSetPassword = async () => {
    if (password.length < 4) return
    try {
      await create.mutateAsync({ password, include_remake: includeRemake })
      setPassword('')
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'message' in err
        ? (err as { message: string }).message
        : t('amazonListing.share.error')
      toast.error(msg)
    }
  }

  const handleToggleRemake = async () => {
    try {
      await create.mutateAsync({ include_remake: !includeRemake })
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'message' in err
        ? (err as { message: string }).message
        : t('amazonListing.share.error')
      toast.error(msg)
    }
  }

  const expiryDate = new Date(expiresAt).toLocaleDateString(
    i18n.language, { day: '2-digit', month: '2-digit', year: 'numeric' }
  )

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
          {t('amazonListing.share.linkLabel')}
        </label>
        <div className="flex gap-2">
          <input
            readOnly
            value={shareUrl}
            className="flex-1 rounded-md border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 px-2 py-1.5 text-xs text-neutral-700 dark:text-neutral-300 truncate"
          />
          <button
            onClick={handleCopy}
            className="shrink-0 rounded-md border border-neutral-200 dark:border-neutral-700 p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-green-500" />
            ) : (
              <Copy className="h-3.5 w-3.5 text-neutral-500" />
            )}
          </button>
        </div>
        {copied && (
          <p className="text-xs text-green-600">{t('amazonListing.share.copied')}</p>
        )}
      </div>

      <p className="text-xs text-neutral-500 dark:text-neutral-400">
        {t('amazonListing.share.expiresAt', { date: expiryDate })}
      </p>

      {hasRemake && (
        <div className="border-t border-neutral-200 dark:border-neutral-700 pt-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeRemake}
              onChange={handleToggleRemake}
              disabled={create.isPending}
              className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500 h-3.5 w-3.5"
            />
            <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
              {t('amazonListing.share.includeRemake')}
            </span>
          </label>
          <p className="text-xs text-neutral-400 mt-1 ml-5.5">
            {t('amazonListing.share.includeRemakeHint')}
          </p>
        </div>
      )}

      <div className="border-t border-neutral-200 dark:border-neutral-700 pt-3">
        <div className="flex items-center gap-2 mb-2">
          {hasPassword
            ? <Lock className="h-3.5 w-3.5 text-amber-500" />
            : <Unlock className="h-3.5 w-3.5 text-neutral-400" />}
          <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
            {hasPassword
              ? t('amazonListing.share.passwordSet')
              : t('amazonListing.share.passwordNone')}
          </span>
        </div>
        <div className="flex gap-2">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('amazonListing.share.passwordPlaceholder')}
            className="flex-1 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-2 py-1.5 text-xs"
            maxLength={72}
          />
          <button
            onClick={handleSetPassword}
            disabled={password.length < 4 || create.isPending}
            className="shrink-0 rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('amazonListing.share.setPassword')}
          </button>
        </div>
      </div>
    </div>
  )
}
